import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Typography,
  Collapse,
  IconButton,
  Chip,
  Autocomplete,
  Stack,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";

// Using regular text inputs for date filtering
import { UserFilters } from "../../../types/admin";

interface UserFiltersPanelProps {
  filters: UserFilters;
  onFiltersChange: (filters: UserFilters) => void;
  onExport?: () => void;
  loading?: boolean;
}

const UserFiltersPanel: React.FC<UserFiltersPanelProps> = ({
  filters,
  onFiltersChange,
  onExport,
  loading = false,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [availableRoles] = useState(["Admin", "Customer", "Shopper", "Cargo"]);
  const [availableCountries] = useState([
    "United States",
    "Canada",
    "United Kingdom",
    "Germany",
    "France",
    "Italy",
    "Spain",
    "Netherlands",
    "Australia",
    "Japan",
  ]);

  const [showClearFilters, setShowClearFilters] = useState(false);

  const handleFilterChange = (field: keyof UserFilters, value: any) => {
    onFiltersChange({
      ...filters,
      [field]: value,
      page: 1, // Reset to first page when filters change
    });
    setShowClearFilters(true);
  };

  const clearFilters = () => {
    onFiltersChange({
      page: 1,
      pageSize: filters.pageSize,
    });
    setShowClearFilters(false);
  };

  const getActiveFiltersCount = () => {
    const filterKeys = Object.keys(filters) as (keyof UserFilters)[];
    return filterKeys.filter((key) => {
      if (key === "page" || key === "pageSize") return false;
      const value = filters[key];
      return value !== undefined && value !== null && value !== "";
    }).length;
  };

  const hasActiveFilters = getActiveFiltersCount() > 0;

  return (
    <div>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Basic Search */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t("admin.userFilters.searchPlaceholder")}
              value={filters.search || ""}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              sx={{ flexGrow: 1 }}
            />

            <Button
              variant="outlined"
              onClick={() => setExpanded(!expanded)}
              startIcon={<FilterIcon />}
              endIcon={<ExpandMoreIcon />}
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            >
              {t("admin.userFilters.filters")} {hasActiveFilters && `(${getActiveFiltersCount()})`}
            </Button>

            {onExport && (
              <Button
                variant="outlined"
                onClick={onExport}
                startIcon={<DownloadIcon />}
                disabled={loading}
              >
                {t("admin.userFilters.export")}
              </Button>
            )}
          </Box>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Box mb={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {filters.role && (
                  <Chip
                    label={`${t("admin.userFilters.role")}: ${filters.role}`}
                    onDelete={() => handleFilterChange("role", "")}
                    size="small"
                  />
                )}
                {filters.isActive !== undefined && (
                  <Chip
                    label={`${t("admin.userFilters.status")}: ${filters.isActive ? t("admin.userFilters.active") : t("admin.userFilters.inactive")}`}
                    onDelete={() => handleFilterChange("isActive", undefined)}
                    size="small"
                  />
                )}
                {filters.isEmailVerified !== undefined && (
                  <Chip
                    label={`${t("admin.userFilters.emailStatus")}: ${filters.isEmailVerified ? t("admin.userFilters.verified") : t("admin.userFilters.unverified")}`}
                    onDelete={() =>
                      handleFilterChange("isEmailVerified", undefined)
                    }
                    size="small"
                  />
                )}
                {filters.authType && (
                  <Chip
                    label={`${t("admin.userFilters.authType")}: ${filters.authType}`}
                    onDelete={() => handleFilterChange("authType", undefined)}
                    size="small"
                  />
                )}
                {filters.country && (
                  <Chip
                    label={`${t("admin.userFilters.country")}: ${filters.country}`}
                    onDelete={() => handleFilterChange("country", "")}
                    size="small"
                  />
                )}
                {showClearFilters && (
                  <Button
                    size="small"
                    onClick={clearFilters}
                    startIcon={<ClearIcon />}
                  >
                    {t("admin.userFilters.clearAll")}
                  </Button>
                )}
              </Stack>
            </Box>
          )}

          {/* Advanced Filters */}
          <Collapse in={expanded}>
            <Box pt={2}>
              <Typography variant="h6" gutterBottom>
                {t("admin.userFilters.advancedFilters")}
              </Typography>

              <Grid container spacing={3}>
                {/* Role Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>{t("admin.userFilters.role")}</InputLabel>
                    <Select
                      value={filters.role || ""}
                      onChange={(e) =>
                        handleFilterChange("role", e.target.value)
                      }
                      label={t("admin.userFilters.role")}
                    >
                      <MenuItem value="">{t("admin.userFilters.allRoles")}</MenuItem>
                      {availableRoles.map((role) => (
                        <MenuItem key={role} value={role}>
                          {role}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Status Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>{t("admin.userFilters.status")}</InputLabel>
                    <Select
                      value={
                        filters.isActive === undefined
                          ? ""
                          : filters.isActive.toString()
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        handleFilterChange(
                          "isActive",
                          value === "" ? undefined : value === "true",
                        );
                      }}
                      label={t("admin.userFilters.status")}
                    >
                      <MenuItem value="">{t("admin.userFilters.allStatuses")}</MenuItem>
                      <MenuItem value="true">{t("admin.userFilters.active")}</MenuItem>
                      <MenuItem value="false">{t("admin.userFilters.inactive")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Email Verification Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>{t("admin.userFilters.emailStatus")}</InputLabel>
                    <Select
                      value={
                        filters.isEmailVerified === undefined
                          ? ""
                          : filters.isEmailVerified.toString()
                      }
                      onChange={(e) => {
                        const value = e.target.value;
                        handleFilterChange(
                          "isEmailVerified",
                          value === "" ? undefined : value === "true",
                        );
                      }}
                      label={t("admin.userFilters.emailStatus")}
                    >
                      <MenuItem value="">{t("admin.userFilters.all")}</MenuItem>
                      <MenuItem value="true">{t("admin.userFilters.verified")}</MenuItem>
                      <MenuItem value="false">{t("admin.userFilters.unverified")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Auth Type Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>{t("admin.userFilters.authType")}</InputLabel>
                    <Select
                      value={filters.authType || ""}
                      onChange={(e) =>
                        handleFilterChange(
                          "authType",
                          e.target.value || undefined,
                        )
                      }
                      label={t("admin.userFilters.authType")}
                    >
                      <MenuItem value="">{t("admin.userFilters.allTypes")}</MenuItem>
                      <MenuItem value="manual">{t("admin.userForm.manualLocal")}</MenuItem>
                      <MenuItem value="google">{t("admin.userForm.google")}</MenuItem>
                      <MenuItem value="admin">{t("admin.userForm.admin")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* Country Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <Autocomplete
                    options={availableCountries}
                    value={filters.country || ""}
                    onChange={(_, value) =>
                      handleFilterChange("country", value || "")
                    }
                    renderInput={(params) => (
                      <TextField {...params} label={t("admin.userFilters.country")} />
                    )}
                    freeSolo
                  />
                </Grid>

                {/* City Filter */}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label={t("admin.userFilters.city")}
                    value={filters.city || ""}
                    onChange={(e) => handleFilterChange("city", e.target.value)}
                  />
                </Grid>

                {/* Date Range Filters */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("admin.userFilters.registrationDateRange")}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("admin.userFilters.fromDate")}
                    type="date"
                    value={filters.createdAfter || ""}
                    onChange={(e) =>
                      handleFilterChange("createdAfter", e.target.value)
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("admin.userFilters.toDate")}
                    type="date"
                    value={filters.createdBefore || ""}
                    onChange={(e) =>
                      handleFilterChange("createdBefore", e.target.value)
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                {/* Last Login Date Range */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("admin.userFilters.lastLoginDateRange")}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("admin.userFilters.lastLoginAfter")}
                    type="date"
                    value={filters.lastLoginAfter || ""}
                    onChange={(e) =>
                      handleFilterChange("lastLoginAfter", e.target.value)
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("admin.userFilters.lastLoginBefore")}
                    type="date"
                    value={filters.lastLoginBefore || ""}
                    onChange={(e) =>
                      handleFilterChange("lastLoginBefore", e.target.value)
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>

                {/* Sort Options */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    {t("admin.userFilters.sortOptions")}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t("admin.userFilters.sortBy")}</InputLabel>
                    <Select
                      value={filters.sortBy || "createdAt"}
                      onChange={(e) =>
                        handleFilterChange("sortBy", e.target.value)
                      }
                      label={t("admin.userFilters.sortBy")}
                    >
                      <MenuItem value="createdAt">{t("admin.userFilters.registrationDate")}</MenuItem>
                      <MenuItem value="lastLoginAt">{t("admin.userFilters.lastLogin")}</MenuItem>
                      <MenuItem value="firstName">{t("admin.userForm.firstName")}</MenuItem>
                      <MenuItem value="lastName">{t("admin.userForm.lastName")}</MenuItem>
                      <MenuItem value="email">{t("admin.userForm.email")}</MenuItem>
                      <MenuItem value="totalOrders">{t("admin.userFilters.totalOrders")}</MenuItem>
                      <MenuItem value="totalSpent">{t("admin.userFilters.totalSpent")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t("admin.userFilters.sortDirection")}</InputLabel>
                    <Select
                      value={filters.sortDirection || "desc"}
                      onChange={(e) =>
                        handleFilterChange(
                          "sortDirection",
                          e.target.value as "asc" | "desc",
                        )
                      }
                      label={t("admin.userFilters.sortDirection")}
                    >
                      <MenuItem value="desc">{t("admin.userFilters.descending")}</MenuItem>
                      <MenuItem value="asc">{t("admin.userFilters.ascending")}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  startIcon={<ClearIcon />}
                >
                  {t("admin.userFilters.clearFilters")}
                </Button>
              </Box>
            </Box>
          </Collapse>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserFiltersPanel;
